import api from './axios';
import { API_ENDPOINTS, getFormName, getSiteGroupId } from '../constants';
import { logError } from '../common/utils/logger';
import { encryptJourneyQueryValue } from '../common/utils/journeyEncryption';
import {
  findPincodeRecordByStateAndCity,
  normalizePincodeInput,
} from '../common/utils/pincodeMasterUtils';
import { getEffectiveApplicationNumber } from '../common/utils/journeyApplicationNumber';

/**
 * Fetch form definition from API by form name and site group ID
 * @param {string} stage - Journey stage (lead, quote, etc.) - defaults to 'lead'
 * @param {Object} additionalParams - Additional parameters to send with request (e.g., PolicyNo)
 * @returns {Promise<Object>} Form definition object
 */
export const fetchFormDefinition = async (stage = 'lead', additionalParams = {}) => {
  try {
    const formName = getFormName(stage);
    const siteGroupId = getSiteGroupId();

    if (!formName) {
      throw new Error(`Form name is not configured for stage "${stage}".`);
    }

    // Allow siteGroupId = 0 (falsy) but reject null/undefined
    if (siteGroupId === null || siteGroupId === undefined) {
      throw new Error('Site group ID is not available. Ensure the app is running within Liferay context.');
    }

    // API expects GET request with formName and siteGroupId as query params
    // Additional params (like PolicyNo) are sent as query params
    const response = await api.get(API_ENDPOINTS.GET_FORM_DEFINITION, {
      params: {
        siteGroupId,
        formName,
        ...additionalParams
      }
    });

    return response?.data || null;
  } catch (error) {
    logError('[fetchFormDefinition] Failed to fetch form definition', error);
    throw error;
  }
};

/**
 * Submit form data for Term Journey Lead
 * @param {Object} formData - Form data to submit
 * @returns {Promise<Object>} Submission response
 */
export const submitFormData = async (formData) => {
  try {
    const payload = { ...(formData || {}) };
    const plainAppNumber = getEffectiveApplicationNumber(payload.applicationNumber);
    if (plainAppNumber) {
      payload.applicationNumber = encryptJourneyQueryValue(plainAppNumber);
    } else if (payload.applicationNumber != null) {
      delete payload.applicationNumber;
    }
    if (payload.dateOfBirth != null && String(payload.dateOfBirth).trim() !== '') {
      payload.dateOfBirth = encryptJourneyQueryValue(payload.dateOfBirth);
    }
    const response = await api.post(API_ENDPOINTS.SUBMIT_LEAD, {
      ...payload
    });

    return response?.data || null;
  } catch (error) {
    logError('[submitFormData] Failed to submit form', error);
    throw error;
  }
};

// In-memory cache for full pincode master (state/city dropdowns + local lookups)
let pincodeCache = null;
let pincodeMasterLoadPromise = null;

const PINCODE_MASTER_TYPE = 'getProductPincodes';
const PINCODE_SUGGESTION_LIMIT = 25;

const normalizePincode = (value) => String(value ?? '').replace(/\D/g, '');

/** Strip non-digits and cap at 6 chars for form inputs. */
export { normalizePincodeInput, extractPincodeLocationFromRecord } from '../common/utils/pincodeMasterUtils';
const getItemPincode = (item) => item?.pincode ?? item?.pinCode ?? '';

const parseJourneyMasterItems = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) return parsed;
      if (Array.isArray(parsed?.items)) return parsed.items;
    } catch {
      // ignore malformed JSON
    }
  }
  return [];
};

const filterPincodeItemsByPrefix = (items, query) =>
  (items || [])
    .filter((item) => {
      const pin = String(getItemPincode(item) ?? '');
      return pin && pin.startsWith(query);
    })
    .sort((a, b) => {
      const aPin = String(getItemPincode(a) ?? '');
      const bPin = String(getItemPincode(b) ?? '');
      const aNum = parseInt(aPin, 10);
      const bNum = parseInt(bPin, 10);
      return !isNaN(aNum) && !isNaN(bNum) ? aNum - bNum : aPin.localeCompare(bPin);
    });

/**
 * Server-side prefix search (3–6 digits). Does not download the full master.
 */
const fetchPincodesByPrefix = async (prefix) => {
  const response = await api.get(API_ENDPOINTS.JOURNEY_MASTERS, {
    params: {
      masterType: PINCODE_MASTER_TYPE,
      pincodePrefix: prefix,
    },
  });
  return parseJourneyMasterItems(response?.data);
};

/**
 * Loads the full pincode master into browser memory and backend PortalCache.
 * Called on lead home page and proposal mount (deduped).
 */
export const fetchPincodeMasterData = async () => {
  if (pincodeCache) return pincodeCache;
  if (pincodeMasterLoadPromise) return pincodeMasterLoadPromise;

  pincodeMasterLoadPromise = api
    .get(API_ENDPOINTS.JOURNEY_MASTERS, {
      params: { masterType: PINCODE_MASTER_TYPE },
    })
    .then((response) => {
      const items = parseJourneyMasterItems(response?.data);
      pincodeCache = items;
      return items;
    })
    .catch((error) => {
      pincodeMasterLoadPromise = null;
      logError('[fetchPincodeMasterData] Failed to load pincode master', error);
      return [];
    });

  return pincodeMasterLoadPromise;
};

/** @deprecated Use fetchPincodeMasterData — kept for existing call sites. */
export const warmPincodeMasterCache = () => fetchPincodeMasterData();

/**
 * Pre-fetch pincode master on lead home page (stores in memory + Redux via fetchPincodeMasterCached).
 */
export const preloadPincodeData = () => fetchPincodeMasterData();

/** Resolve a 6-digit pin from lead / journey blobs (same keys as Lead.jsx). */
export const getLeadPincodeValue = (lead = {}) => {
  const directCandidates = [
    lead.pincode,
    lead.pinCode,
    lead.communicationPincode,
    lead.communicationPinCode,
    lead.residencyPincode,
    lead.residencyPinCode,
  ];
  for (const value of directCandidates) {
    const normalized = normalizePincodeInput(value);
    if (normalized.length === 6) return normalized;
  }
  const embedded = String(lead.cityAndPincode ?? '').match(/\b(\d{6})\b/);
  return embedded?.[1] ?? '';
};

export const getLeadStateValue = (lead = {}) => {
  const raw =
    lead.state ??
    lead.residencyState ??
    lead.communicationState ??
    lead.branchState ??
    '';
  return String(raw ?? '').trim();
};

export const getLeadCityValue = (lead = {}) => {
  const raw = lead.city ?? lead.residencyCity ?? lead.communicationCity ?? '';
  const trimmed = String(raw ?? '').trim();
  if (trimmed) return trimmed;
  const cityAndPincode = String(lead.cityAndPincode ?? '');
  const slashPart = cityAndPincode.split('/')[0]?.trim();
  return slashPart && !/^\d+$/.test(slashPart) ? slashPart : '';
};

export const getPincodeMasterRecord = async (pincode) => {
  const normalizedPincode = normalizePincode(pincode);
  if (!normalizedPincode) return null;

  if (normalizedPincode.length >= 3) {
    try {
      const items = await fetchPincodesByPrefix(normalizedPincode);
      const exact = items.find(
        (item) =>
          normalizePincode(getItemPincode(item)) === normalizedPincode,
      );
      if (exact) return exact;
      // Prefix API for 6-digit eq filter returns a single row — use it directly.
      if (normalizedPincode.length === 6 && items.length === 1) {
        return items[0];
      }
      if (normalizedPincode.length === 6) {
        return null;
      }
    } catch (error) {
      logError('[getPincodeMasterRecord] Prefix lookup failed', error);
    }
  }

  const items = await fetchPincodeMasterData();
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    items.find((item) => normalizePincode(getItemPincode(item)) === normalizedPincode) ??
    null
  );
};

/**
 * Fetch pincode suggestions based on query
 * @param {string} pincodeQuery - Pincode query string (minimum 3 characters)
 * @returns {Promise<Array>} Array of matching pincode suggestions
 */
export const fetchPincodeSuggestions = async (pincodeQuery) => {
  const query = normalizePincode(pincodeQuery);
  if (query.length < 3) return [];

  try {
    const items = await fetchPincodesByPrefix(query);
    return items.slice(0, PINCODE_SUGGESTION_LIMIT);
  } catch (error) {
    logError('[fetchPincodeSuggestions] Prefix search failed', error);
    try {
      const items = await fetchPincodeMasterData();
      return filterPincodeItemsByPrefix(items, query).slice(0, PINCODE_SUGGESTION_LIMIT);
    } catch (fallbackError) {
      logError('[fetchPincodeSuggestions] Failed to fetch suggestions', fallbackError);
      return [];
    }
  }
};

const ORGANIZATION_MASTER_TYPE = 'organizationdetailsmasters';
const ORGANIZATION_SUGGESTION_LIMIT = 25;
/** Minimum typed length before organization prefix / exact-match API calls. */
export const ORGANIZATION_PREFIX_MIN_LENGTH = 3;

/**
 * Server-side organization search. Filters from backend cache (contains match).
 */
const fetchOrganizationsByPrefix = async (prefix, { exact = false } = {}) => {
  const response = await api.get(API_ENDPOINTS.JOURNEY_MASTERS, {
    params: {
      masterType: ORGANIZATION_MASTER_TYPE,
      organizationPrefix: String(prefix ?? '').trim(),
      ...(exact ? { organizationExact: true } : {}),
    },
  });
  return parseJourneyMasterItems(response?.data);
};

/**
 * Lookup pincode record by state + city (same as former bulk-cache .find()).
 */
export const getPincodeByStateAndCity = async (state, city) => {
  const pincodeState = String(state ?? '').trim();
  const pincodeCity = String(city ?? '').trim();
  if (!pincodeState || !pincodeCity) return null;

  if (Array.isArray(pincodeCache) && pincodeCache.length > 0) {
    const local = findPincodeRecordByStateAndCity(
      pincodeCache,
      pincodeState,
      pincodeCity,
    );
    if (local) return local;
  }

  try {
    const response = await api.get(API_ENDPOINTS.JOURNEY_MASTERS, {
      params: {
        masterType: PINCODE_MASTER_TYPE,
        pincodeState,
        pincodeCity,
      },
    });
    const items = parseJourneyMasterItems(response?.data);
    return items[0] ?? null;
  } catch (error) {
    logError('[getPincodeByStateAndCity] Lookup failed', error);
    return null;
  }
};

/**
 * Fetch organization suggestions based on query (any non-empty input).
 * @param {string} organizationQuery - Organization name query
 * @returns {Promise<Array>} Array of matching organization master records
 */
export const fetchOrganizationSuggestions = async (organizationQuery) => {
  const prefix = String(organizationQuery ?? '').trim();
  if (!prefix || prefix.length < ORGANIZATION_PREFIX_MIN_LENGTH) return [];

  try {
    const items = await fetchOrganizationsByPrefix(prefix);
    return items.slice(0, ORGANIZATION_SUGGESTION_LIMIT);
  } catch (error) {
    logError('[fetchOrganizationSuggestions] Prefix search failed', error);
    return [];
  }
};

/**
 * Checks whether an organisation name exactly matches a listed master record.
 * @param {string} orgName - Organisation name to verify
 * @returns {Promise<boolean>}
 */
export const checkOrganisationListed = async (orgName) => {
  const needle = String(orgName ?? '').trim();
  if (
    !needle ||
    needle.length < ORGANIZATION_PREFIX_MIN_LENGTH ||
    needle.toLowerCase() === 'others'
  ) {
    return false;
  }

  try {
    const items = await fetchOrganizationsByPrefix(needle, { exact: true });
    return items.some(
      (item) =>
        String(item?.organizationName ?? '').trim().toLowerCase() ===
        needle.toLowerCase(),
    );
  } catch (error) {
    logError('[checkOrganisationListed] Lookup failed', error);
    return false;
  }
};